/**
=========================================================
* Argon Dashboard 2 PRO MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-pro-mui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-router-dom components

// prop-types is a library for typechecking of props

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Notistack for snackbar
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";

// Argon Dashboard 2 PRO MUI components
import ArgonBox from "components/ArgonBox";
import ArgonButton from "components/ArgonButton";
import ArgonTypography from "components/ArgonTypography";

function BackgroundBlogCard({ color, image, title, description, action }) {
  // Get enqueueSnackbar function from useSnackbar hook
  const { enqueueSnackbar } = useSnackbar();

  // Handle popup action
  const handlePopupClick = () => {
    enqueueSnackbar(action.message, {
      variant: action.variant || "info",
      anchorOrigin: action.position || { vertical: "bottom", horizontal: "center" },
    });
  };

  return (
    <Card raised>
      <ArgonBox
        sx={({
          functions: { linearGradient, rgba },
          palette: { gradients },
          borders: { borderRadius },
        }) => ({
          backgroundImage: gradients[color]
            ? `${linearGradient(
                rgba(gradients[color].main, 0.3),
                rgba(gradients[color].state, 0.5)
              )}, url(${image})`
            : `${linearGradient(
                rgba(gradients.dark.main, 0.3),
                rgba(gradients.dark.state, 0.4)
              )}, url(${image})`,
          backgroundSize: "cover",
          borderRadius: borderRadius.lg,
          p: 3,
          paddingY: "30px",
        })}
      >
        <ArgonBox mb={1}>
          <ArgonTypography variant="h6" color="white" fontWeight="bold" textTransform="capitalize">
            {title}
          </ArgonTypography>
        </ArgonBox>
        <ArgonBox mb={2}>
          <ArgonTypography variant="body2" color="white">
            {description}
          </ArgonTypography>
        </ArgonBox>
        {action.type === "internal" ? (
          <ArgonButton component={Link} to={action.route} variant="outlined" color="white" circular>
            {action.label} &nbsp; <Icon>arrow_forward</Icon>
          </ArgonButton>
        ) : action.type === "popup" ? (
          <ArgonButton onClick={handlePopupClick} variant="outlined" color="white" circular>
            {action.label} &nbsp; <Icon>notifications</Icon>
          </ArgonButton>
        ) : (
          <ArgonButton
            component="a"
            href={action.route}
            target="_blank"
            rel="noreferrer"
            variant="outlined"
            color="white"
            circular
          >
            {action.label} &nbsp; <Icon>arrow_forward</Icon>
          </ArgonButton>
        )}
      </ArgonBox>
    </Card>
  );
}

// Setting default values for the props of BackgroundBlogCard
BackgroundBlogCard.defaultProps = {
  color: "dark",
};

// Typechecking props for the BackgroundBlogCard
BackgroundBlogCard.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  action: PropTypes.shape({
    type: PropTypes.oneOf(["external", "internal", "email", "popup"]).isRequired,
    route: PropTypes.string,
    label: PropTypes.string.isRequired,
    message: PropTypes.string, // For popup type - the message to display
    variant: PropTypes.oneOf(["default", "error", "success", "warning", "info"]), // Optional snackbar variant
    position: PropTypes.object, // Optional position config for the snackbar
  }).isRequired,
};

export default BackgroundBlogCard;
